import { CircleAlert, CircleCheck, Info, X } from 'lucide-react'
import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { cx } from './cx'

type Kind = 'success' | 'error' | 'info'

type ToastOptions = {
  /** e.g. { label: 'Rückgängig', onClick: undo } — clicking also dismisses. */
  action?: { label: string; onClick: () => void }
  /** ms until auto-dismiss; default 5000. Paused while hovered or focused. */
  duration?: number
}

type ToastItem = { id: number; kind: Kind; message: string } & ToastOptions

let items: ToastItem[] = []
let nextId = 1
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((l) => l())
const MAX_VISIBLE = 3

function push(kind: Kind, message: string, options: ToastOptions = {}) {
  const id = nextId++
  // Same message twice in a row (e.g. several failing refetches) shows once.
  items = [...items.filter((t) => t.message !== message), { id, kind, message, ...options }].slice(-MAX_VISIBLE)
  emit()
  return id
}

function dismiss(id: number) {
  items = items.filter((t) => t.id !== id)
  emit()
}

/** Short German sentences, never raw server text. Returns the id for `toast.dismiss`. */
export const toast = {
  success: (message: string, options?: ToastOptions) => push('success', message, options),
  error: (message: string, options?: ToastOptions) => push('error', message, options),
  info: (message: string, options?: ToastOptions) => push('info', message, options),
  dismiss,
}

const icons = { success: CircleCheck, error: CircleAlert, info: Info }
const iconTone = { success: 'text-positive', error: 'text-negative', info: 'text-ink-3' }

function ToastView({ t }: { t: ToastItem }) {
  const Icon = icons[t.kind]
  // Hover or focus pauses the countdown (WCAG 2.2.1); the remaining time survives the pause.
  const [paused, setPaused] = useState(false)
  const remaining = useRef(t.duration ?? 5000)

  useEffect(() => {
    if (paused) return
    const started = Date.now()
    const timer = setTimeout(() => dismiss(t.id), remaining.current)
    return () => {
      clearTimeout(timer)
      remaining.current -= Date.now() - started
    }
  }, [paused, t.id])

  return (
    <li className="pointer-events-auto w-full animate-rise md:w-[380px]">
      <div
        onPointerEnter={() => setPaused(true)}
        onPointerLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        className="flex items-center gap-3 rounded-card border border-line bg-surface py-2.5 pr-2 pl-4 text-small text-ink shadow-float"
      >
        <Icon aria-hidden="true" className={cx('size-[18px] shrink-0', iconTone[t.kind])} strokeWidth={2} />
        <p className="min-w-0 flex-1 py-1">
          {/* Kind prefix for screen readers; sighted users get the icon. */}
          {t.kind === 'error' && <span className="sr-only">Fehler: </span>}
          {t.message}
        </p>
        {t.action && (
          <button
            type="button"
            onClick={() => {
              t.action?.onClick()
              dismiss(t.id)
            }}
            className="h-8 shrink-0 rounded-[6px] px-2.5 font-semibold text-ink hover:bg-bg-hover"
          >
            {t.action.label}
          </button>
        )}
        <button
          type="button"
          aria-label="Hinweis schließen"
          onClick={() => dismiss(t.id)}
          className="grid size-8 shrink-0 place-items-center rounded-[6px] text-ink-3 hover:bg-bg-hover hover:text-ink"
        >
          <X aria-hidden="true" className="size-4" />
        </button>
      </div>
    </li>
  )
}

const subscribe = (l: () => void) => {
  listeners.add(l)
  return () => listeners.delete(l)
}

/** Mount once near the root. Bottom-centre above the tab bar on phones, bottom-right on desktop. */
export function Toaster() {
  const list = useSyncExternalStore(subscribe, () => items, () => items)
  const ref = useRef<HTMLElement>(null)
  // A manual popover lives in the top layer, so toasts stay visible above an open modal <dialog>. It stays
  // open while empty so the live region exists before the first message (late regions are not announced).
  // Re-showing moves it above a dialog opened after it; only done then, as it re-creates the region.
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const open = el.matches(':popover-open')
    if (open && list.length && document.querySelector('dialog:modal')) el.hidePopover()
    if (!el.matches(':popover-open')) el.showPopover()
  }, [list])
  return (
    <section
      ref={ref}
      popover="manual"
      aria-label="Hinweise"
      className="pointer-events-none inset-auto inset-x-0 bottom-0 m-0 h-auto w-full overflow-visible border-0 bg-transparent p-0"
    >
      <ol
        aria-live="polite"
        aria-relevant="additions"
        className="flex flex-col items-center gap-2 px-4 pb-[calc(env(safe-area-inset-bottom)+5rem)] md:items-end md:px-6 md:pb-6 lg:pb-6"
      >
        {list.map((t) => (
          <ToastView key={t.id} t={t} />
        ))}
      </ol>
    </section>
  )
}
